import React, { useState, useRef, useContext, useLayoutEffect } from 'react';
import Style from './chat.tailwind';
import { useApiContext } from 'app/api';
import { FileInput, Loader } from 'components/lib';
import { FilesViewer } from './filesViewer';
import { useTranslate } from 'app/translations';
import { useQueryClient } from 'react-query';
import { Icon, ViewContext, UserAvatar, initialsFromName, cn } from 'components/lib';
import { AuthContext } from 'app/auth';

export const Chat = (props) => {
    const { _t } = useTranslate();
    const viewContext = useContext(ViewContext);
    const { user } = useContext(AuthContext);
    const { ticket } = props;
    const { integration_data } = ticket;
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const service = ticket?.ticket_category?.integration_data?.service;
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [message, setMessage] = useState('');
    const { callApi } = useApiContext();

    const fileInputRef = useRef(null);
    const textAreaRef = useRef(null);

    // Grow with the content up to the CSS max-height, then scroll inside.
    useLayoutEffect(() => {
        const el = textAreaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [message]);

    const handleChange = (event) => {
        setMessage(event.target.value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        let formData = new FormData();

        const action_type = message.trim() === '' ? 'attachment_added' : 'comment_posted';

        formData.append('comment', message);
        formData.append('ticket_id', ticket.id);
        formData.append('action_type', action_type);
        formData.append('fromValetify', true);
        formData.append(
            'integration_data',
            JSON.stringify({
                ...ticket?.ticket_category?.integration_data,
                task_id: integration_data?.task,
            }),
        );

        // Append each file separately
        uploadedFiles.forEach((file, index) => {
            formData.append(`file${index}`, file.data);
        });

        const res = await callApi({
            url: `/api/ticket/history/comment?service=${service}&ticket=${ticket.id}`,
            method: 'post',
            data: formData,
        });

        setIsSubmitting(false);

        if (res.status === 200) {
            queryClient.refetchQueries('tickets');
            props.onPosted?.();
            setMessage('');
            setUploadedFiles([]);
            resetFileInput();
        } else {
            // keep the draft so a failed send doesn't lose what was typed
            viewContext.notification.show(res.message || _t('error.tryAgain'), 'error', true);
        }
    };

    const removeFile = (indexToRemove) => {
        setUploadedFiles((prevFiles) => prevFiles.filter((file, index) => index !== indexToRemove));
    };
    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (!disableSubmitConditions()) handleSubmit(event);
        }
    };
    React.useEffect(() => {
        return () => {
            resetFileInput();
        };
    }, []);
    const handleFileChange = (name, file, valid) => {
        if (valid === false) return;
        setUploadedFiles((prevUploadedFiles) => [...prevUploadedFiles, ...file]);
    };

    const disableSubmitConditions = () => {
        return (uploadedFiles.length === 0 && message.trim() === '') || isSubmitting;
    };

    const authorName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';

    return (
        <div className={Style.container}>
            <FileInput
                onChange={handleFileChange}
                ref={fileInputRef}
                classes={'absolute invisible'}
            />

            <UserAvatar
                name={authorName}
                initials={initialsFromName(authorName)}
                size="md"
                className={Style.avatar}
                fallbackClassName="text-[11px]"
            />

            <form
                onSubmit={handleSubmit}
                className={cn(Style.frame, isSubmitting && 'opacity-90')}>
                <div className="relative">
                    <textarea
                        ref={textAreaRef}
                        onKeyDown={handleKeyDown}
                        className={Style.textArea}
                        rows={3}
                        placeholder={_t('ticket.history.writeComment')}
                        onChange={handleChange}
                        value={message}
                        disabled={isSubmitting}
                    />
                    {isSubmitting && (
                        <div className={Style.submittingOverlay}>
                            <Loader />
                        </div>
                    )}
                </div>

                {uploadedFiles.length > 0 && (
                    <div className={Style.files}>
                        <FilesViewer files={uploadedFiles} onRemoveFile={removeFile} />
                    </div>
                )}

                <div className={Style.toolbar}>
                    <button
                        type="button"
                        className={Style.attachBtn}
                        onClick={() => fileInputRef.current.click()}
                        disabled={isSubmitting}
                        aria-label={_t('ticket.history.file')}>
                        <Icon image="paperclip" color="currentColor" size={15} />
                        <span className={Style.attachLabel}>{_t('ticket.history.file')}</span>
                    </button>

                    <span className={Style.spacer} />
                    <span className={Style.hint}>{_t('ticket.detail.composerHint')}</span>

                    <button
                        type="submit"
                        className={Style.submitBtn}
                        disabled={disableSubmitConditions()}>
                        <Icon image="send" color="currentColor" size={14} />
                        {_t('ticket.history.send')}
                    </button>
                </div>
            </form>
        </div>
    );
};
